import { Injectable } from '@angular/core';
import { AppDatabase } from './libraries/database/database';

export enum LocalStorageEnum {
  WidgetOptions = 'widgetOptions',
  SnapshotOptions = 'snapshotOptions',
  Locale = 'locale',
  GroupRestriction = 'GroupRestriction',
  UserAppId = 'userAppId',
  RememberMeEmail = 'rememberMeEmail',
  LabelConversionSeen = 'labelConversionSeen',
  DefaultGroupEdit = 'defaultGroupEdit',
  DefaultCollectionId = 'defaultCollectionId',
  InternetExplorerWarning = 'internetExplorerWarning',
  GettingStartedTutorial = 'gettingStartedTutorial',
  AutoCapture = 'autoCapture',
  SystemMessage = 'systemMessage',
  ExploreRedesignMessage = 'exploreRedesignMessage',
  Iphone12Warning = 'iphone12Warning',
  RecentRecipes = 'recentRecipes',
  TeamTrialExpirationSeen = 'teamTrialExpiredSeen',
  ProExpirationSeen = 'proExpirationSeen',
  PaintPalettes = 'paintPalettes',
  RecentSearchConfig = 'recentSearchConfig',
}

export enum SessionStorageEnum {
  UserMessagesSeen = 'userMessagesSeen',
  PreviewSize = 'previewSize',
}

export enum DatabaseStorageEnum {
  PreviewStory = 'previewStory',
  PreviewBookmark = 'previewBookmark',
}

class StorageFallback implements Storage {
  private _data: { [key: string]: string } = {};

  get keys(): string[] {
    return Object.keys(this._data);
  }

  get length(): number {
    return this.keys.length;
  }

  clear() {
    this._data = {};
  }

  getItem(key: string): string | null {
    return this._data[key];
  }

  key(index: number): string | null {
    return this.keys[index];
  }

  removeItem(key: string) {
    delete this._data[key];
  }

  setItem(key: string, value: string) {
    this._data[key] = value;
  }
}

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private _localStorage: Storage;
  private _sessionStorage: Storage;
  private _indexedStorage: AppDatabase;

  constructor() {
    this._localStorage = this.getStorage('localStorage');
    this._sessionStorage = this.getStorage('sessionStorage');
    this._indexedStorage = new AppDatabase();
  }

  getLocalItem(key: LocalStorageEnum) {
    return this._localStorage.getItem(`${key}`);
  }

  getLocalJSON(key: LocalStorageEnum) {
    const value = this.getLocalItem(key);
    return value ? JSON.parse(value) : null;
  }

  setLocalItem(key: LocalStorageEnum, value: any): boolean {
    const serializedValue = typeof value === 'object' ? JSON.stringify(value) : `${value}`;
    try {
      this._localStorage.setItem(`${key}`, serializedValue);
      return true;
    } catch (e) {
      return false;
    }
  }

  removeLocalItem(key: LocalStorageEnum) {
    this._localStorage.removeItem(`${key}`);
  }

  getSessionItem<T>(key: SessionStorageEnum): T {
    const entry = this._sessionStorage.getItem(`${key}`);
    if (entry) {
      return <T>JSON.parse(entry);
    } else {
      return null;
    }
  }

  setSessionItem(key: SessionStorageEnum, entry: any): boolean {
    const json = JSON.stringify(entry);
    try {
      this._sessionStorage.setItem(`${key}`, json);
      return true;
    } catch (e) {
      return false;
    }
  }

  removeSessionItem(key: SessionStorageEnum) {
    this._sessionStorage.removeItem(`${key}`);
  }

  setDatabaseItem(key: DatabaseStorageEnum, value: any) {
    return this._indexedStorage.keyValue.put(value, key);
  }

  getDatabaseItem(key: DatabaseStorageEnum) {
    return this._indexedStorage.keyValue.get(key);
  }

  clearStorage() {
    this.clearLocalStorage();
    this.clearSessionStorage();
  }

  clearLocalStorage() {
    this._localStorage.clear();
  }

  clearSessionStorage() {
    this._sessionStorage.clear();
  }

  // check storage support and/or access, else inline fallback
  private getStorage(key: 'localStorage' | 'sessionStorage'): Storage {
    try {
      return window[key] || new StorageFallback();
    } catch (e) {
      return new StorageFallback();
    }
  }
}
